import { useCallback, useEffect, useMemo, useState } from 'react';

import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Button, Collapse } from '@mui/material';
import { Box, Tooltip } from '@kc-console/common-ui';
import { useRouter, useScopedLinkBuilder } from '@kc-console/context';
import { EllipsisText } from '@kc-console/shared-ui';

import useMenuActiveState from '../../../../hooks/useMenuActiveState';
import EllipsisIcon from '../icon/EllipsisIcon';
import ListToggleIcon from '../icon/ListToggleIcon';
import { getCollapsedLnbItemButtonStyle, getLnbItemButtonStyle } from '../styled';

import type { Menus } from '@kc-console/context';

interface Props {
  menu: Menus;
  collapsed?: boolean;
  depth?: number;
  onClick?: () => void;
  subMenu?: Menus[] | null;
}

type ItemButtonProps = Pick<Props, 'collapsed' | 'depth'> & {
  hasSubMenu?: boolean;
};

interface ItemContentProps {
  collapsed: boolean;
  hasSubMenu: boolean;
  isOpen: boolean;
  isSelected: boolean;
  menuName: string;
}

function ItemContent({ collapsed, menuName, isSelected, hasSubMenu, isOpen }: ItemContentProps) {
  if (collapsed) {
    return <IconBox>{hasSubMenu ? <ListToggleIcon open={isOpen} /> : <EllipsisIcon />}</IconBox>;
  }

  return (
    <>
      <EllipsisText
        variant={isSelected ? 'label/semibold/md' : 'label/medium/md'}
        direction="vertical"
        message={menuName}
      >
        {menuName}
      </EllipsisText>
      {hasSubMenu && <ListToggleIcon open={isOpen} />}
    </>
  );
}

export default function ServiceListItem({
  depth = 1,
  collapsed = false,
  menu,
  onClick,
  ...others
}: Props) {
  const router = useRouter();
  const { path, name: menuName, subMenus, attribute } = menu;
  const { isDefaultOpen = false } = attribute ?? {};

  const isSelected = useMenuActiveState(menu);
  const { getProps } = useScopedLinkBuilder();

  const hasSubMenu = (subMenus?.length ?? 0) > 0;

  const [isOpen, setOpen] = useState(
    hasSubMenu && (router.asPath.startsWith(path) || isDefaultOpen),
  );

  const isChildActive = useMemo(() => {
    if (!hasSubMenu) return false;
    const currentPath = router.asPath.split('?')[0];
    return currentPath.startsWith(path);
  }, [hasSubMenu, router.asPath, path]);

  const isActive = isSelected || (!isOpen && isChildActive);

  const handleToggle = useCallback(() => {
    setOpen(prev => !prev);
    onClick?.();
  }, [onClick]);

  const buttonProps = hasSubMenu
    ? { LinkComponent: 'button' as const, onClick: handleToggle }
    : { LinkComponent: 'a' as const, ...getProps(path, { onClick }) };

  useEffect(() => {
    // 모든 서비스 등 LNB 메뉴 외 다른 동작에서 경로 변경 시 서브메뉴가 열려있지 않다면 열리도록 처리
    if (hasSubMenu && (router.asPath.startsWith(path) || isDefaultOpen)) {
      setOpen(true);
    }
  }, [router.asPath, path, hasSubMenu, isDefaultOpen]);

  return (
    <>
      <Tooltip message={collapsed ? menuName : ''} placement="right">
        <ItemButton
          {...buttonProps}
          collapsed={collapsed}
          depth={depth}
          hasSubMenu={hasSubMenu}
          disableRipple
          data-active={isActive ? 'active' : undefined}
          {...others}
        >
          <ItemContent
            collapsed={collapsed}
            menuName={menuName}
            isSelected={isActive}
            hasSubMenu={hasSubMenu}
            isOpen={isOpen}
          />
        </ItemButton>
      </Tooltip>
      {hasSubMenu && (
        <Collapse in={isOpen} timeout="auto">
          {subMenus?.map(subMenu => (
            <ServiceListItem
              key={subMenu.path}
              depth={depth + 1}
              menu={subMenu}
              collapsed={collapsed}
              onClick={onClick}
            />
          ))}
        </Collapse>
      )}
    </>
  );
}

const IconBox = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: 24px;
  transition: 200ms;
`;

// TODO: common-ui에서 LinkComponent props를 아직 제공하지 않고 있어서 mui 사용
const ItemButton = styled(Button, {
  shouldForwardProp: prop => !['collapsed', 'depth', 'hasSubMenu'].includes(prop as string),
})<ItemButtonProps>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-width: auto;
  min-height: 40px;
  max-height: 60px;
  border-radius: 0;
  text-transform: none;
  text-align: left;

  ${({ collapsed, depth }) => {
    if (collapsed) {
      return css`
        padding: 0px 16px;
      `;
    }

    switch (depth) {
      case 3:
        return css`
          padding: 9px 16px 9px 56px;
        `;
      case 2:
        return css`
          padding: 9px 16px 9px 40px;
        `;
      default:
        return css`
          padding: 9px 16px 9px 24px;
        `;
    }
  }}

  ${({ theme, collapsed, hasSubMenu }) =>
    collapsed
      ? getCollapsedLnbItemButtonStyle(theme, { hasSubMenu })
      : getLnbItemButtonStyle(theme)}
`;
